import {
  type MRT_ColumnFiltersState,
  type MRT_SortingState,
  MRT_ColumnDef,
  MRT_RowData,
  MRT_TableState,
  MRT_VisibilityState,
  MRT_TableOptions,
} from "mantine-react-table"
import { Input, Output } from "../../../../@client/api"
import { useUserLocalStorage } from "../use-user-local-storage"
import { InfinitScrollingTable } from "."
import { PaginatedListRequest } from "../infinit-scrolling-list"
import { ExclusiveProperties } from "../../../../utils/exclusive-properties"
import { PaginatedData } from "../../../../@client/Theasoft_ApiServer__Utils_Pagination"
import { PaginatedTableRequest } from "../../../../@client/Theasoft_ApiServer__Utils_Pagination"
import { useIsSmallScreen } from "../../../../utils/useIsSmallScreen"

type KeysOfType<T, U> = {
  [K in keyof T]: T[K] extends U ? K : never
}[keyof T]

type VirtualTableUrls = KeysOfType<Output, PaginatedData<unknown>>

export type SearchValue<URL extends VirtualTableUrls> = ExclusiveProperties<
  Input[URL],
  PaginatedTableRequest
> & {
  globalFilter: string
  columnFilters: MRT_ColumnFiltersState
  sorting: MRT_SortingState
}

export interface SearchProps<URL extends VirtualTableUrls> {
  value: SearchValue<URL>
  onChange: (value: SearchValue<URL>) => void
}

interface Props<
  URL extends VirtualTableUrls,
  TData extends MRT_RowData = Output[URL]["data"],
> {
  url: URL
  columnDefinitions: MRT_ColumnDef<TData>[]
  partialInput: ExclusiveProperties<Input[URL], PaginatedTableRequest>
  onClickRow?: (row: TData) => void
  defaultSorting: MRT_SortingState
  defaultColumVisibility?: MRT_VisibilityState
  tableOptions?: Omit<MRT_TableOptions<TData>, "data" | "columns">
  searchProps?: SearchProps<URL>
  tableState?: Partial<MRT_TableState<TData>>
  forceTableState?: boolean
}

export interface ExtendedTableProps<
  URL extends VirtualTableUrls,
  TData extends MRT_RowData = Output[URL]["data"],
> extends Omit<
    Props<URL, TData>,
    "url" | "columnDefinitions" | "defaultColumnVisibility" | "defaultSorting"
  > {}

export function ExtendedInfinitScrollingTable<
  URL extends VirtualTableUrls,
  TData extends MRT_RowData = Output[URL]["data"],
>({
  url,
  partialInput,
  columnDefinitions,
  onClickRow,
  defaultSorting,
  defaultColumVisibility,
  tableOptions: options,
  searchProps,
  tableState: initialTableState,
  forceTableState,
}: Props<URL, TData>) {
  const [globalFilter, setGlobalFilter] = React.useState<string>(
    searchProps?.value.globalFilter || ""
  )
  const [columnFilters, setColumnFilters] =
    React.useState<MRT_ColumnFiltersState>(
      searchProps?.value.columnFilters || []
    )
  const [sorting, setSorting] = React.useState<MRT_SortingState>(
    searchProps?.value.sorting || defaultSorting
  )
  const [tableState, setTableState] = useUserLocalStorage<
    Partial<MRT_TableState<TData>>
  >({
    key: url + "table-state",
    defaultValue: {
      ...initialTableState,
      columnVisibility: defaultColumVisibility ?? {},
    },
  })

  const [columnVisibility, setColumnVisibility] = React.useState(
    tableState.columnVisibility
  )

  React.useEffect(() => {
    setTableState({ ...tableState, columnVisibility: columnVisibility })
  }, [columnVisibility])

  const isSmallScreen = useIsSmallScreen()

  const tableOptions = React.useMemo<Omit<MRT_TableOptions<TData>, "data">>(
    () => ({
      enableColumnDragging: false,
      enableColumnOrdering: true,
      enableColumnActions: false,
      enableDensityToggle: false,
      columns: columnDefinitions,
      manualFiltering: true,
      manualSorting: true,
      enableColumnResizing: true,
      enableHeaderActionsHoverReveal: true,
      onColumnFiltersChange: (updaterOrValue) =>
        typeof updaterOrValue === "function"
          ? setColumnFilters((prev) => updaterOrValue(prev ?? []))
          : setColumnFilters(updaterOrValue),
      onGlobalFilterChange: (updaterOrValue) =>
        typeof updaterOrValue === "function"
          ? setGlobalFilter((prev) => updaterOrValue(prev ?? []))
          : setGlobalFilter(updaterOrValue),
      onSortingChange: (updaterOrValue) =>
        typeof updaterOrValue === "function"
          ? setSorting((prev) => updaterOrValue(prev ?? []))
          : setSorting(updaterOrValue),
      onShowGlobalFilterChange: (updaterOrValue) =>
        setTableState((prev) => ({
          ...prev,
          showGlobalFilter:
            typeof updaterOrValue === "function"
              ? updaterOrValue(prev.showGlobalFilter ?? false)
              : updaterOrValue,
        })),
      onColumnVisibilityChange: (updaterOrValue) =>
        setColumnVisibility((prev) =>
          typeof updaterOrValue === "function"
            ? updaterOrValue(prev ?? {})
            : updaterOrValue
        ),
      onColumnSizingChange: (updaterOrValue) =>
        setTableState((prev) => ({
          ...prev,
          columnSizing:
            typeof updaterOrValue === "function"
              ? updaterOrValue(prev.columnSizing ?? {})
              : updaterOrValue,
        })),
      onColumnOrderChange: (updaterOrValue) => {
        setTableState((prev) => ({
          ...prev,
          columnOrder:
            typeof updaterOrValue === "function"
              ? updaterOrValue(prev.columnOrder ?? [])
              : updaterOrValue,
        }))
      },
      onDensityChange: (updaterOrValue) =>
        setTableState((prev) => ({
          ...prev,
          density:
            typeof updaterOrValue === "function"
              ? updaterOrValue(prev.density ?? "md")
              : updaterOrValue,
        })),
      state: {
        ...tableState,
        columnFilters,
        globalFilter,
        sorting,
        columnVisibility: columnVisibility,
        ...(forceTableState ? initialTableState : {}),
      },
      ...options,
    }),
    [
      tableState,
      options,
      columnDefinitions,
      sorting,
      isSmallScreen,
      columnVisibility,
    ]
  )

  const extendedTableInput = React.useMemo<
    ExclusiveProperties<PaginatedTableRequest, PaginatedListRequest>
  >(
    () => ({
      columnFilters: JSON.stringify(columnFilters),
      globalFilter: globalFilter,
      sorting: JSON.stringify(sorting),
    }),
    [partialInput, columnFilters, globalFilter, sorting]
  )

  React.useEffect(() => {
    if (searchProps) {
      searchProps.onChange({
        ...searchProps.value,
        globalFilter,
        columnFilters,
        sorting,
      })
    }
  }, [globalFilter, columnFilters, sorting])

  return (
    <InfinitScrollingTable
      tableOptions={tableOptions}
      url={url}
      partialInput={
        { ...partialInput, ...extendedTableInput } as ExclusiveProperties<
          Input[URL],
          PaginatedListRequest
        >
      }
      onClickRow={onClickRow}
    />
  )
}
